import { orgQueryKeys } from '@/config/queryKeys';
import { createOrganization } from '@/services/organizations';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrgFormApi } from './formTypes';
import type { OrgFormValues } from './schema';
import { createOrgSchema } from './schema';

export function useCreateOrganization(): { form: OrgFormApi; hasAttemptedSubmit: boolean } {
  const { t } = useTranslation('orgs');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const schema = useMemo(() => createOrgSchema(t), [t]);

  const form = useForm({
    formId: 'organizationCreate',
    defaultValues: {
      name: '',
      address: '',
      alias: '',
      primaryContact: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      itContact: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      },
      domain: '',
      subscription: {
        active: true,
        expiration: '',
      },
    } as OrgFormValues,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      try {
        // Map OrgFormValues to API OrgCreate shape
        const payload = {
          name: value.name,
          email_domain: value.domain,
          alias: value.alias,
          address: value.address,
          primary_contact: {
            first_name: value.primaryContact.firstName,
            last_name: value.primaryContact.lastName,
            phone: value.primaryContact.phone,
            email: value.primaryContact.email,
          },
          it_contact: {
            first_name: value.itContact.firstName,
            last_name: value.itContact.lastName,
            phone: value.itContact.phone,
            email: value.itContact.email,
          },
          subscription: {
            is_active: value.subscription.active,
            expires_at: value.subscription.expiration,
          },
        };
        await createOrganization(payload);
        toast.success(t('create_org_success'));
        await queryClient.invalidateQueries({
          queryKey: orgQueryKeys.list(),
        });
        form.reset();
        await navigate({ to: '/organizations' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : undefined;
        toast.error(errorMessage ? t('create_org_error_detail', { message: errorMessage }) : t('create_org_error'));
      }
    },
    onSubmitInvalid: () => {
      requestAnimationFrame(() => {
        const firstInvalid = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
        firstInvalid?.focus();
      });
    },
  });

  const hasAttemptedSubmit = useStore(form.baseStore, s => s.submissionAttempts > 0);

  return { form: form as unknown as OrgFormApi, hasAttemptedSubmit };
}
